import {
  getItemStatusCode,
  isBrPendingCode,
  getBrExpiryDateMinIso,
  normalizeNameForCompare,
} from './brDocument';

/**
 * 提交确认页：表单是否可提交（与 UI 校验一致）。
 */

function approvalIdKey(item) {
  const id = item?.approvalId ?? item?.ApprovalId;
  return id == null || id === '' ? '' : String(id);
}

function resolveRegistrationNoForDuplicate(item, sourceItems = []) {
  const direct = normalizeNameForCompare(
    item?.registrationNo || item?.RegistrationNo,
  );
  if (direct) {
    return direct;
  }
  const sourceApprovalId = item?.sourceApprovalId ?? item?.SourceApprovalId;
  const sourceBrId = item?.sourceBusinessRegistrationId
    ?? item?.SourceBusinessRegistrationId;
  const source = (Array.isArray(sourceItems) ? sourceItems : []).find((row) => {
    if (sourceApprovalId != null
      && String(row?.approvalId ?? row?.ApprovalId ?? '') === String(sourceApprovalId)) {
      return true;
    }
    if (sourceBrId != null
      && String(row?.businessRegistrationId ?? row?.BusinessRegistrationId ?? '')
        === String(sourceBrId)) {
      return true;
    }
    return false;
  });
  return normalizeNameForCompare(
    source?.registrationNo || source?.RegistrationNo,
  );
}

/**
 * 同公司列表中：相同 Registration No. 且状态为审批中 / 人工审批中的记录。
 * 含执照待审，以及 nameChanges 中的更名待审（GetList 不含待审更名卡）。
 */
export function findPendingBrDuplicatesByRegistrationNo(
  items,
  registrationNo,
  nameChanges = [],
) {
  const target = normalizeNameForCompare(registrationNo);
  if (!target) {
    return [];
  }
  const list = Array.isArray(items) ? items : [];
  const fromList = list.filter((item) => {
    if (!isBrPendingCode(getItemStatusCode(item))) {
      return false;
    }
    const no = resolveRegistrationNoForDuplicate(item, list);
    return no && no === target;
  });

  const seen = new Set(fromList.map(approvalIdKey).filter(Boolean));
  const changes = Array.isArray(nameChanges) ? nameChanges : [];
  const fromNameChanges = changes
    .filter((row) => isBrPendingCode(getItemStatusCode(row)))
    .filter((row) => {
      const no = resolveRegistrationNoForDuplicate(row, list);
      if (!no || no !== target) {
        return false;
      }
      const key = approvalIdKey(row);
      if (key && seen.has(key)) {
        return false;
      }
      if (key) {
        seen.add(key);
      }
      return true;
    })
    .map((row) => ({
      ...row,
      registrationNo: row.registrationNo || row.RegistrationNo
        || resolveRegistrationNoForDuplicate(row, list),
    }));

  return [...fromList, ...fromNameChanges];
}

export function canSubmitBrConfirmForm(form, scanBlockerKeys = []) {
  if ((scanBlockerKeys || []).length) {
    return false;
  }
  return !Object.keys(getBrConfirmFieldErrors(form)).length;
}

/**
 * 确认页字段级错误（用于禁用提交等）。
 * 企业名 / 注册号的红字展示由表单「触碰后再显示」，避免扫描空提取立刻报 required。
 */
export function getBrConfirmFieldErrors(form) {
  const errors = {};
  if (!form?.registeredCompanyName?.trim()) {
    errors.registeredCompanyName = true;
  }
  if (!form?.registrationNo?.trim()) {
    errors.registrationNo = true;
  }
  if (!form?.displayNameEn?.trim()) {
    errors.displayNameEn = true;
  }
  if (!form?.displayNameZh?.trim()) {
    errors.displayNameZh = true;
  }
  if (form?.hasExpirationDate) {
    const start = String(form.startDate || '').trim();
    const end = String(form.expirationDate || '').trim();
    const minExpiry = getBrExpiryDateMinIso();
    // 起迄必填；生效日不晚于到期日；到期日 ≥ 今天
    if (!start || !end || start > end || end < minExpiry) {
      errors.datePeriod = true;
    }
  }
  return errors;
}

export function createEmptyBrConfirmForm() {
  return {
    registeredCompanyName: '',
    registrationNo: '',
    hasExpirationDate: false,
    startDate: '',
    expirationDate: '',
    displayNameEn: '',
    displayNameZh: '',
  };
}
